/* eslint-disable unicorn/no-null, no-magic-numbers, @typescript-eslint/no-unnecessary-condition, react/jsx-sort-props, @typescript-eslint/no-confusing-void-expression */

import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import type {
    ImperativePanelGroupHandle,
    ImperativePanelHandle
} from 'react-resizable-panels';
import { Box } from '@mantine/core';
import { modals } from '@mantine/modals';
import { ProjectVersionStatus } from '@newo/api/gateway';
import type { TProjectVersion } from '@newo/api/gateway';
import useDesignerFilesStore from './DesignerFiles.store';
import { useProjectVersionsQuery } from './ProjectVersions.queries';
import { useMintSessionMutation } from './Files.queries';
import VersionsSidebar from './VersionsSidebar';
import { formatVersionLabel } from './VersionsSidebar/VersionsSidebar.utils';
import FileTree from './FileTree';
import EditorArea from './EditorArea';
import { autoSelectVersion, getDirtyFilePaths } from './FilesTab.utils';
import {
    FILE_TREE_DEFAULT_SIZE,
    SIDEBAR_COLLAPSED_SIZE,
    SIDEBAR_DEFAULT_SIZE
} from './FilesTab.constants';
import UnsavedVersionSwitchModal from './UnsavedVersionSwitchModal';
import { useSaveDirtyFilesWithConflictResolution } from './useSaveDirtyFilesWithConflictResolution';
import styles from './FilesTab.module.css';

interface FilesTabProps {
    projectIdn?: string;
}

const FilesTab: FC<FilesTabProps> = ({ projectIdn }) => {
    const sidebarRef = useRef<ImperativePanelHandle>(null);
    const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);

    const fileTreeSizeRef = useRef(FILE_TREE_DEFAULT_SIZE);
    const expandedSidebarSizeRef = useRef(SIDEBAR_DEFAULT_SIZE);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const selectedVersionId = useDesignerFilesStore(
        (state) => state.selectedVersionId
    );
    const openFiles = useDesignerFilesStore((state) => state.openFiles);
    const setSelectedVersionId = useDesignerFilesStore(
        (state) => state.setSelectedVersionId
    );

    const versions = useProjectVersionsQuery(projectIdn);
    const mintSession = useMintSessionMutation();
    const saveDirtyFilesWithConflictResolution =
        useSaveDirtyFilesWithConflictResolution();

    const switchToVersion = (versionId: string) => {
        mintSession.mutate(versionId);
    };

    const saveDirtyFilesAndSwitch = async (
        dirtyFilePaths: string[],
        versionId: string
    ): Promise<void> => {
        const isSaved =
            await saveDirtyFilesWithConflictResolution(dirtyFilePaths);
        if (!isSaved) {
            return;
        }
        switchToVersion(versionId);
    };

    useEffect(() => {
        if (!versions.data) {
            return;
        }
        if (versions.data.some((version) => version.id === selectedVersionId)) {
            return;
        }

        const computed = autoSelectVersion(versions.data);
        if (!computed) {
            setSelectedVersionId(null);
            return;
        }

        mintSession.mutate(computed.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [versions.data, selectedVersionId]);

    const selectedVersion =
        versions.data?.find((v) => v.id === selectedVersionId) ?? null;
    const isReadOnly =
        selectedVersion?.status === ProjectVersionStatus.Published;

    const toggleSidebar = () => {
        const isCollapsed = sidebarRef.current?.isCollapsed() ?? false;
        const sidebarNext = isCollapsed
            ? expandedSidebarSizeRef.current
            : SIDEBAR_COLLAPSED_SIZE;
        const fileTree = fileTreeSizeRef.current;
        const editor = Math.max(0, 100 - sidebarNext - fileTree);
        panelGroupRef.current?.setLayout([sidebarNext, fileTree, editor]);
    };

    const handleVersionSelect = (version: TProjectVersion) => {
        if (selectedVersionId === version.id) {
            return;
        }

        const dirtyFilePaths = getDirtyFilePaths(openFiles);

        if (dirtyFilePaths.length > 0) {
            const id = modals.open({
                title: 'Unsaved changes',
                centered: true,
                children: (
                    <UnsavedVersionSwitchModal
                        dirtyFilePaths={dirtyFilePaths}
                        onDiscard={() => {
                            modals.close(id);
                            switchToVersion(version.id);
                        }}
                        onKeepEditing={() => {
                            modals.close(id);
                        }}
                        onSave={() => {
                            modals.close(id);
                            void saveDirtyFilesAndSwitch(
                                dirtyFilePaths,
                                version.id
                            );
                        }}
                    />
                )
            });
        } else {
            switchToVersion(version.id);
        }
    };

    return (
        <Box className={styles.wrapper}>
            <PanelGroup
                className={styles.panels}
                direction="horizontal"
                ref={panelGroupRef}
            >
                <Panel
                    collapsedSize={SIDEBAR_COLLAPSED_SIZE}
                    collapsible
                    defaultSize={SIDEBAR_DEFAULT_SIZE}
                    maxSize={30}
                    minSize={12}
                    ref={sidebarRef}
                    onCollapse={() => setIsSidebarCollapsed(true)}
                    onExpand={() => setIsSidebarCollapsed(false)}
                    onResize={(size) => {
                        if (size > SIDEBAR_COLLAPSED_SIZE + 1) {
                            expandedSidebarSizeRef.current = size;
                        }
                    }}
                >
                    <VersionsSidebar
                        isCollapsed={isSidebarCollapsed}
                        projectIdn={projectIdn}
                        selectedVersionId={selectedVersionId}
                        onToggle={toggleSidebar}
                        onVersionSelect={handleVersionSelect}
                    />
                </Panel>
                <PanelResizeHandle className="w-0.5 bg-gray-3" />
                <Panel
                    defaultSize={FILE_TREE_DEFAULT_SIZE}
                    minSize={13}
                    onResize={(size) => {
                        fileTreeSizeRef.current = size;
                    }}
                >
                    <FileTree
                        isLoadingSession={mintSession.isPending}
                        isReadOnly={isReadOnly ?? false}
                        versionId={selectedVersionId}
                        versionLabel={
                            selectedVersion
                                ? formatVersionLabel(selectedVersion)
                                : null
                        }
                    />
                </Panel>
                <PanelResizeHandle className="w-0.5 bg-gray-3" />
                <Panel minSize={25}>
                    <EditorArea isReadOnly={isReadOnly ?? false} />
                </Panel>
            </PanelGroup>
        </Box>
    );
};

export default FilesTab;
